import _                                        from 'underscore';
import React, { FC, useContext, useLayoutEffect, useState } from 'react';
import classNames                               from 'classnames';
import { ClassValue }                           from 'classnames/types';
import { Link }                                 from 'react-router-dom';
import { Cell, Grid }                           from 'styled-css-grid';

import { Align, CorvalUIColor, SemanticUIColor } from '../../types';

import { RouterUtility }             from '../../util';
import { useUIDisplayModes }         from '../../hooks';
import { UI_DISPLAY_MODES }          from '../../constants/UIEnums';
import { ErrorBoundary }             from '../components/util/errorHandling/ErrorBoundary';
import { UserActivityMonitor }       from '../components/util/access/UserActivityMonitor';
import { MainNav }                   from '../components/nav/MainNav';
import { UserAccountContextualizer } from '../components/user-account-contextualizer/UserAccountContextualizer';
import { CorvalLogo }                from '../components/product/CorvalLogo';
import { DisplayModeControl }        from '../components/menus/DisplayModeControl';
import { StyledContainer }           from './StyledContainer';
import { Layout }                    from './Layout';
import { PageSidebar }               from './PageSidebar';
import { PageFooter }                from './PageFooter';
import { CorvalAssistantIconLink }   from "@src/views/components/product/CorvalAssistantIconLink";
import { TourContext } from '../contexts';

interface PageProps {
    name?: string,
    className?: ClassValue,
    fadeIn?: boolean,
    showTop?: boolean,
    showBrand?: boolean,
    showUser?: boolean,
    showNav?: boolean,
    showCorvalAssistant?: boolean,
    showToggleMode?: boolean,
    padMainVertical?: boolean,
    padMainHorizontal?: boolean,
    allowContentScroll?: boolean,
    monitorUserActivity?: boolean,
    toggleModes?: UI_DISPLAY_MODES[],
    ignoreDisplaySettings?: boolean,
    onCorvalAssistantClick?: () => void,
}

const defaultProps: PageProps = {
    name:                   null,
    className:              null,
    fadeIn:                 false,
    showTop:                true,
    showBrand:              true,
    showUser:               true,
    showNav:                true,
    showCorvalAssistant:    false,
    showToggleMode:         true,
    padMainVertical:        true,
    padMainHorizontal:      true,
    allowContentScroll:     false,
    monitorUserActivity:    true,
    toggleModes:            [ UI_DISPLAY_MODES.CHROME_MINIMIZED ],
    ignoreDisplaySettings:  false,
    onCorvalAssistantClick: undefined
};

const Page: FC<PageProps> = ( props ) => {
	const { setShowTour } = useContext(TourContext);

    const handleStartTour = () => {
        setShowTour(true); // Enable the tour
    };
    let { children, className, name }                                  = props,
        { fadeIn, padMainHorizontal, padMainVertical }                 = props,
        { showTop, showNav, showBrand, showUser, showCorvalAssistant } = props,
        { allowContentScroll, monitorUserActivity }                    = props,
        { showToggleMode, toggleModes, ignoreDisplaySettings }         = props,
        { onCorvalAssistantClick }                                     = props,
        [ sidebarOffset, setSidebarOffset ]                            = useState( 0 ),
        { minimizedChrome }                                            = useUIDisplayModes();

    let displayModeToggle = ( showToggleMode && _.isArray( toggleModes ) && toggleModes.length > 0 );

    useLayoutEffect( () => {

        setSidebarOffset( getSidebarVericalOffset() );
    } )

    let containerClasses = classNames( {
            'page':                true,
            'fade-in':             fadeIn,
            'debug':               false,
            [ `page--${ name }` ]: name,
        },
        className,
    );

    let layoutClasses = classNames( {
        'layout--frame-chromeless':       ( !showTop && !showNav ),
        'layout--frame-chrome-minimized': minimizedChrome && !ignoreDisplaySettings,
        'fade-in':                        minimizedChrome && !ignoreDisplaySettings,
    } );

    let mainContentClasses = classNames( {
        'padding-md-horizontal': padMainHorizontal,
        'padding-lg-vertical':   padMainVertical,
        'overflow-content--y':   allowContentScroll,
    } );

    let getSidebarVericalOffset = (): number => {

        let elementHeights = [] as number[],
            elementList    = [
                'layout--frame-top',
            ];

        elementList.map( element => {

            /* Get element from page */
            let [ el ] = document.getElementsByClassName( element );

            /* Push height if available */
            if( el ){ elementHeights.push( el.getClientRects()[ 0 ].height ) }
        } )

        return _.reduce( elementHeights, ( sum, elHeight ) => ( sum + elHeight ), 0 );
    }

    return (
        <>
            { monitorUserActivity && <UserActivityMonitor/> }

            { displayModeToggle && <DisplayModeControl toggleModes={ [ UI_DISPLAY_MODES.CHROME_MINIMIZED, ...props.toggleModes ] }/> }

            <StyledContainer className={ containerClasses } bgColor={ CorvalUIColor.WHITE }>

                <PageSidebar>

                    <Layout name={ 'frame' } className={ layoutClasses }>

                        { showTop && (
                            <Layout name={ 'frame-top' } bgColor={ SemanticUIColor.BLUE }>

                                {/* BRANDING */ }
                                { showBrand && (
                                    <Layout name={ 'frame-brand' }>
                                        <div onClick={handleStartTour} >
                                            <CorvalLogo wrapped  className={ 'margin-xs-vertical' } size={ 'small' }/>
                                        </div>
                                    </Layout>
                                ) }


                                {/* MAIN NAV */ }
                                { showNav && (
                                    <Layout name={ 'frame-nav' } textAlign={ Align.LEFT }>

                                        <MainNav/>

                                    </Layout>
                                ) }

                                {/* USER */ }
                                { showUser && (
                                    <Layout name={ 'frame-user' }>


                                        <Grid columns={ `auto auto` } columnGap={ '10px' }>

                                            { showCorvalAssistant && (
                                                <Cell middle>

                                                    <CorvalAssistantIconLink onClick={ onCorvalAssistantClick ?? undefined }/>

                                                </Cell>
                                            ) }

                                            <Cell middle>

                                                <UserAccountContextualizer/>

                                            </Cell>
                                        </Grid>

                                    </Layout>
                                ) }

                            </Layout> ) }

                        <Layout name={ 'frame-main' } fadeIn className={ mainContentClasses }>

                            <ErrorBoundary>
                                { children }
                            </ErrorBoundary>

                        </Layout>

                        <Layout name={ 'frame-legal' } bgColor={ CorvalUIColor.DARKBLUE }>

                            <PageFooter/>

                        </Layout>

                    </Layout>

                </PageSidebar>

            </StyledContainer>

        </>
    );
}

Page.defaultProps = defaultProps;

export { Page }
